import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { useUserRole } from "../../../context/UserRoleContext";
import { useBar } from "../../../context/BarContext";
import { useBarOwnerData } from "../../../context/BarOwnerDataContext";
import DashboardLayout from "../../../components/common/DashboardLayout";
import MyArtists from "../components/artists/MyArtists";
import FindCovers from "../components/artists/FindCovers";
import FindArtists from "../components/artists/FindArtists";

const libraries = ["places"];

const Artists = () => {
  const { loading } = useUserRole();
  const { isLoaded: isMapsLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries,
  });
  const { selectedBar } = useBar();
  const {
    ensureOwnerArtistsLoaded,
    getManagementArtists,
    getBars,
    fetchBars,
    fetchFindArtists,
    getFindArtists,
    invalidateCache,
    fetchManagementArtists,
  } = useBarOwnerData();
  const [activeTab, setActiveTab] = useState("my-artists");

  useEffect(() => {
    ensureOwnerArtistsLoaded();
    const barsCache = getBars();
    if (!barsCache.bars?.length && !barsCache.loading) {
      fetchBars();
    }
  }, [ensureOwnerArtistsLoaded, getBars, fetchBars]);

  const {
    artists: ownerArtists,
    associations,
    loading: ownerLoading,
  } = getManagementArtists();

  const myArtistsFiltered = useMemo(() => {
    if (!selectedBar?.bar_id) return [];
    return ownerArtists.filter((a) =>
      (associations[a.artist_id] || []).some(
        (assoc) => assoc.bar_id === selectedBar.bar_id
      )
    );
  }, [ownerArtists, associations, selectedBar?.bar_id]);

  const refetchMyArtists = useCallback(() => {
    invalidateCache("managementArtists");
    fetchManagementArtists();
  }, [invalidateCache, fetchManagementArtists]);

  const getFindArtistsData = useCallback(
    (barId, searchParams = {}) => {
      return getFindArtists(barId, searchParams);
    },
    [getFindArtists]
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  const tabs = [
    { id: "my-artists", label: "My Artists", icon: "🎤" },
    { id: "find-covers", label: "Find Covers", icon: "🎵" },
    { id: "find-artists", label: "Find Artists", icon: "🔍" },
  ];

  return (
    <DashboardLayout>
      <div className="bar-owner-dashboard">
        {/* Tab Selector */}
        <div className="tab-selector">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content - Keep all components mounted but conditionally visible */}
        <div className="tab-content">
          <div
            style={{ display: activeTab === "my-artists" ? "block" : "none" }}
          >
            <MyArtists
              artists={myArtistsFiltered}
              associations={associations}
              loading={ownerLoading}
              onRefetch={refetchMyArtists}
              activeTab={activeTab}
              selectedBar={selectedBar}
              isMapsLoaded={isMapsLoaded}
            />
          </div>
          <div
            style={{ display: activeTab === "find-covers" ? "block" : "none" }}
          >
            <FindCovers
              isMapsLoaded={isMapsLoaded}
              associations={associations}
            />
          </div>
          <div
            style={{ display: activeTab === "find-artists" ? "block" : "none" }}
          >
            <FindArtists
              onFetchArtists={fetchFindArtists}
              getCachedData={getFindArtistsData}
              activeTab={activeTab}
              isMapsLoaded={isMapsLoaded}
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Artists;
